import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { AuthProvider, useAuth } from '@/auth/AuthContext';
import { AdminGuard } from '@/components/auth/AdminGuard';
import { ConversionsPage } from '@/features/conversions/ConversionsPage';
import { FamiliesPage } from '@/features/families/FamiliesPage';
import { FamilyDetailPage } from '@/features/families/FamilyDetailPage';
import { EditIngredientPage } from '@/features/ingredients/EditIngredientPage';
import { IngredientsPage } from '@/features/ingredients/IngredientsPage';
import { NewIngredientPage } from '@/features/ingredients/NewIngredientPage';
import { EditTechniquePage } from '@/features/techniques/EditTechniquePage';
import { NewTechniquePage } from '@/features/techniques/NewTechniquePage';
import { TechniquesPage } from '@/features/techniques/TechniquesPage';
import { UserDetailPage } from '@/features/users/UserDetailPage';
import { UsersPage } from '@/features/users/UsersPage';
import { queryClient } from '@/lib/query-client';
import { initSentry, Sentry } from '@/lib/sentry';
import { DashboardPage } from '@/pages/DashboardPage';
import { LoginPage } from '@/pages/LoginPage';

import '@/index.css';

initSentry();

// ---------------------------------------------------------------------------
// Simple client-side router based on window.location.pathname
// ---------------------------------------------------------------------------
function Router() {
  const path = window.location.pathname;

  // Parse dynamic segments
  const ingredientEditMatch = path.match(/^\/ingredients\/([^/]+)\/edit$/);
  const techniqueEditMatch = path.match(/^\/techniques\/([^/]+)\/edit$/);
  const userDetailMatch = path.match(/^\/users\/([^/]+)$/);
  const familyDetailMatch = path.match(/^\/families\/([^/]+)$/);

  if (path === '/login') return <LoginPage />;

  if (
    ingredientEditMatch ||
    techniqueEditMatch ||
    userDetailMatch ||
    familyDetailMatch ||
    path === '/' ||
    path.startsWith('/ingredients') ||
    path.startsWith('/techniques') ||
    path.startsWith('/conversions') ||
    path.startsWith('/users') ||
    path.startsWith('/families')
  ) {
    return (
      <AdminGuard>
        {(() => {
          if (ingredientEditMatch) return <EditIngredientPage />;
          if (path === '/ingredients/new') return <NewIngredientPage />;
          if (path.startsWith('/ingredients')) return <IngredientsPage />;
          if (techniqueEditMatch) return <EditTechniquePage />;
          if (path === '/techniques/new') return <NewTechniquePage />;
          if (path.startsWith('/techniques')) return <TechniquesPage />;
          if (path.startsWith('/conversions')) return <ConversionsPage />;
          if (userDetailMatch) return <UserDetailPage />;
          if (path.startsWith('/users')) return <UsersPage />;
          if (familyDetailMatch) return <FamilyDetailPage />;
          if (path.startsWith('/families')) return <FamiliesPage />;
          return <DashboardPage />;
        })()}
      </AdminGuard>
    );
  }

  return <LoginPage />;
}

// ---------------------------------------------------------------------------
// Auth-aware router wrapper: auto-redirect to / if logged in as admin
// ---------------------------------------------------------------------------
function AuthRouter() {
  const { isSignedIn, isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-green-600 border-t-transparent" />
      </div>
    );
  }

  const path = window.location.pathname;
  if (path === '/login' && isSignedIn && isAdmin) {
    window.location.replace('/');
    return null;
  }

  return <Router />;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------
const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

createRoot(rootEl).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>An error occurred.</p>}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AuthRouter />
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
